import {Component,OnDestroy,OnInit,ViewChild} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {FraisFacturesService} from "@components/admin/frais/factures/frais-factures.service";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {Session} from "@domain/security/session";
import {ActivatedRoute,Router} from "@angular/router";
import {isRouteAllowed} from "@core/security/role-admin-helpers";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";
import {IComponentWithRoutedTabs} from "@domain/admin/recherche/component-with-routed-tabs";
import {BehaviorSubject,Subscription} from "rxjs";
import {FloatingButtonAction} from "@share/component/floating-button/floating-button";
import {TypeCharge} from "@domain/prestation/type-charge";
import {NgForm} from "@angular/forms";

/**
 * Enum pour les noms des différents onglets de la page factures
 */
export enum Onglets {
    RELEVES = "Releves",
    FOURNISSEURS = "Fournisseurs",
    TYPES_CHARGES = "TypesCharges",
    TOLERANCES = "Tolerances"
}

/**
 * Sous-menu factures
 *
 * @author Guillaume TRAVOT
 * @date 21/11/2023
 */
@Component({
    host: {'data-test-id': 'frais-factures'},
    templateUrl: './frais-factures.component.html',
    providers: [FraisFacturesService]
})
export class FraisFacturesComponent implements OnInit, OnDestroy, IComponentWithRoutedTabs {
    /** Liste statique des onglets */
    public static listeOnglets: Array<PageHeaderItem> = [
        {
            code: Onglets.RELEVES,
            libelle: 'admin.frais.factures.onglets.releves',
            url: 'Admin/Frais/Factures/Releves'
        }, {
            code: Onglets.FOURNISSEURS,
            libelle: 'admin.frais.factures.onglets.fournisseurs',
            url: 'Admin/Frais/Factures/Fournisseurs'
        }, {
            code: Onglets.TYPES_CHARGES,
            libelle: 'admin.frais.factures.onglets.typesCharges',
            url: 'Admin/Frais/Factures/TypesCharges'
        }, {
            code: Onglets.TOLERANCES,
            libelle: 'admin.frais.factures.onglets.tolerances',
            url: 'Admin/Frais/Factures/Tolerances'
        }
    ];

    /** Liste des différents onglets disponibles dans le menu frais */
    listeTabItems: Array<PageHeaderItem> = FraisFacturesComponent.listeOnglets;

    /** Onglet courant */
    selectedItem: PageHeaderItem;

    /** Référence vers l'enum des onglets entreprise pour l'utiliser dans le template */
    Onglet = Onglets;

    /** Index de l'onglet courant */
    selectedIndex: number;

    /** Indique si le header doit être affiché */
    isDisplayed: boolean;

    /** Indique s'il faut afficher le formulaire en création ou en édition */
    isCreation: boolean;

    /** Indique si on est dans la page d'ajout ou de modification d'un type de charge */
    isTypeChargeDetail: boolean = false;

    /** Type de charge à afficher */
    typeCharge: TypeCharge;

    /** Session */
    private session: Session;

    /** Souscription */
    private subscription: Subscription;

    /** Liste des actions possibles */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Formulaire */
    @ViewChild('form') form: NgForm;

    constructor(
        protected translateService: TranslateService,
        private router: Router,
        private store: Store<AppState>,
        private activatedRoute: ActivatedRoute,
        private fraisFacturesService: FraisFacturesService,
    ) { }

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Récupération de la session
        this.store.select(state => state.session).pipe(filterFirstNotNull()).subscribe((session: Session) => {
            //Stockage de la session
            this.session = session;

            //Filtrage des onglets autorisés
            this.listeTabItems = this.listeTabItems.filter(i => isRouteAllowed(this.router, this.session, i.url));

            this.subscription = this.activatedRoute.url.subscribe(() => {
                //Récupération de la route
                const firstChild = this.activatedRoute.snapshot.firstChild;

                //Extraction du path
                const path = firstChild.url[0].path;

                //On vérifie si on trouve l'onglet demandé via l'url
                const matching = this.listeTabItems.find(i => i.code === path);

                //Si on trouve bien l'onglet correspondant
                if (!!matching) {
                    //on met à jour le composant header
                    setTimeout(() => {
                        this.selectedItem = matching;
                        this.selectedIndex = this.listeTabItems.findIndex(i => i.code === path);
                        this.isDisplayed = true;
                    });
                } else {
                    //Sinon, on redirige sur le premier onglet et on met à jour le composant header
                    setTimeout(() => {
                        this.selectedItem = this.listeTabItems[0];
                        this.selectedIndex = 0;
                        this.isDisplayed = true;
                    });
                }
            });
        });
    }

    /**
     * Changement d'onglet
     *
     * @param selectedItem Onglet sélectionné
     */
    onSelectedItemChange(selectedItem: PageHeaderItem): void {
        if (!this.selectedItem) {
            this.selectedItem = selectedItem;
        }
        if (selectedItem?.code !== this.selectedItem?.code) {
            this.selectedItem = selectedItem;
            this.router.navigateByUrl(selectedItem.url);
        }
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy() {
        //Résiliation de l'abonnement
        this.subscription.unsubscribe();
    }

    /**
     * Indique si on est dans le détail d'un objet
     */
    get isInDetail() {
        return this.fraisFacturesService.isShowDetail;
    }
}
